import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import useModal from '@/hooks/useModal';
import SvgSelector from '@/components/Svg/SvgSelector';
import { FontStyle } from '@/utils/StyleUtil';

type SnoozePolicy =
    | { type: 'todayMidnight' }
    | { type: 'nextWeekMonday' }
    | { type: 'nextMonthStart' }
    | { type: 'afterNDaysAtMidnight'; days: number }
    | { type: 'until'; at: number }; // ms timestamp

const toMidnight = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

const nextMidnight = (d: Date) => {
    const x = new Date(d);
    x.setHours(24, 0, 0, 0);
    return x;
};

const addDays = (d: Date, n: number) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
};

const getNextWeekMondayMidnight = (now: Date) => {
    const d0 = toMidnight(now);
    const day = d0.getDay(); // 0=Sun..6=Sat
    let delta = (8 - day) % 7; // 다음 월요일까지 남은 일수
    if (delta === 0) delta = 7; // 오늘이 월요일이면 '차주' 월요일
    return addDays(d0, delta); // 00:00 유지
};

const getNextMonthStartMidnight = (now: Date) => new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);

/** 정책 → 다시 보여줄 시각(ms) */
function computeResumeAt(policy: SnoozePolicy, base = new Date()): number {
    switch (policy.type) {
        case 'todayMidnight':
            return nextMidnight(base).getTime();
        case 'nextWeekMonday':
            return getNextWeekMondayMidnight(base).getTime();
        case 'nextMonthStart':
            return getNextMonthStartMidnight(base).getTime();
        case 'afterNDaysAtMidnight': {
            // 내일 0시를 기준으로 (days-1)일 더함 → "정확히 N일 뒤 0시"
            const start = nextMidnight(base);
            return addDays(start, Math.max(0, policy.days - 1)).getTime();
        }
        case 'until':
            return policy.at;
        default:
            return nextMidnight(base).getTime();
    }
}

const storageAPI = {
    get: (key: string) => (typeof window === 'undefined' ? null : window.localStorage.getItem(key)),
    set: (key: string, val: string) => typeof window !== 'undefined' && window.localStorage.setItem(key, val),
    remove: (key: string) => typeof window !== 'undefined' && window.localStorage.removeItem(key),
};

type Props = {
    storageKey: string;
    // eslint-disable-next-line no-unused-vars
    Comp: (helpers: { close: () => void }) => ReactNode;
    policy?: SnoozePolicy; // 기본: 오늘 하루(내일 0시)
    label?: string; // 기본: '오늘 하루 다시 보지 않기'
};

const DailyPopupModal = ({ storageKey, Comp, policy, label }: Props) => {
    const { addModal, removeCurrentModal } = useModal();
    const effectivePolicy = useMemo<SnoozePolicy>(() => policy ?? { type: 'todayMidnight' }, [policy]);
    const effectiveLabel = label ?? '오늘 하루 다시 보지 않기';

    const openModal = useCallback(() => {
        addModal({
            key: 'popUp',
            props: {
                type: 'default',
                containerStyle: { borderRadius: '20px', overflow: 'hidden' },
                contentStyle: { padding: 0 },
                content: (
                    <Modal
                        onClose={removeCurrentModal}
                        onSnooze={() => {
                            const resumeAt = computeResumeAt(effectivePolicy);
                            storageAPI.set(storageKey, String(resumeAt));
                            removeCurrentModal();
                        }}
                        label={effectiveLabel}
                    >
                        {Comp({ close: removeCurrentModal })}
                    </Modal>
                ),
            },
        });
    }, [addModal, removeCurrentModal, Comp, storageKey, effectivePolicy, effectiveLabel]);

    // 마운트 시 열지 여부 판단
    useEffect(() => {
        const raw = storageAPI.get(storageKey);
        const now = Date.now();

        if (!raw) {
            openModal();
            return;
        }
        const resumeAt = Number(raw);
        if (!Number.isFinite(resumeAt) || now >= resumeAt) {
            storageAPI.remove(storageKey);
            openModal();
        }
    }, [openModal, storageKey]);

    return null;
};

const Modal = ({
    children,
    label,
    onClose,
    onSnooze,
}: {
    children: ReactNode;
    label: string;
    onClose: () => void;
    onSnooze: () => void;
}) => {
    const [checked, setChecked] = useState(false);

    const handleClose = useCallback(() => {
        if (checked) onSnooze();
        else onClose();
    }, [checked, onClose, onSnooze]);

    return (
        <Container>
            <CloseIcon onClick={handleClose}>
                <SvgSelector svg="close" width={18} height={18} strokeWidth={1.6} stroke="#222222" />
            </CloseIcon>

            {children}

            <Footer>
                <CheckboxButton onClick={() => setChecked(v => !v)}>
                    <SvgSelector
                        svg="check-circle"
                        width={22}
                        height={22}
                        fill={checked ? 'var(--checked-color)' : '#dadada'}
                        style={{ marginRight: 5 }}
                    />
                    <span>{label}</span>
                </CheckboxButton>
                <CloseButton onClick={handleClose}>닫기</CloseButton>
            </Footer>
        </Container>
    );
};

const Container = styled.div`
    position: relative;
    width: 320px;
`;

const CloseIcon = styled.button`
    position: absolute;
    top: 20px;
    right: 20px;
    width: 28px;
    height: 28px;
    cursor: pointer;
    background: transparent;
`;

const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    position: relative;
    padding: 16px 20px;
    background-color: #ffffff;
`;

const CheckboxButton = styled.button`
    display: inline-flex;
    align-items: center;
    border: 0;
    padding: 0;
    background: none;
    ${FontStyle(14, 22, '400', '#888888', -1)};
`;

const CloseButton = styled.button`
    border: 0;
    padding: 0;
    background: none;
    text-decoration: underline;
    text-underline-position: from-font;
    ${FontStyle(14, 22, '500', '#acacac', -1.2)};
`;

export default DailyPopupModal;
